import { cn } from "@faire/prototype-playground/lib/utils";
import { Heart } from "@faire/slate-icons/Heart";
import { HeartSolid } from "@faire/slate-icons/HeartSolid";
import { tsv } from "@faire/slate/lib/tsv";
import { motion, useReducedMotion } from "framer-motion";
import React from "react";

interface SaveHeartButtonProps {
  isSaved: boolean;
  onToggle: () => void;
  /** Hides the heart until the enclosing `group` is hovered. */
  hoverOnly?: boolean;
  /** `medium` for the search grid, `small` for the condensed rail tiles. */
  size?: "small" | "medium";
  /** Absolute positioning, which differs per tile. */
  className?: string;
  ariaLabel: string;
}

/**
 * The save control shared by the search grid and the condensed rail tiles.
 *
 * Extracted rather than duplicated: the two tiles are separate forks, and every
 * previous tweak to this heart had to be applied twice or they drifted apart.
 */
export const SaveHeartButton: React.FC<SaveHeartButtonProps> = ({
  isSaved,
  onToggle,
  hoverOnly = false,
  size = "medium",
  className,
  ariaLabel,
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isSaved}
      className={cn(
        buttonStyle.cls({ size }),
        hoverOnly && revealStyle.cls({ saved: isSaved }),
        className
      )}
      onClick={(event) => {
        // The enclosing tile can be clickable; saving is not navigating.
        event.stopPropagation();
        onToggle();
      }}
      type="button"
    >
      {isSaved ? (
        // The filled heart mounts fresh on save, so the spring plays once, on
        // the way in only — un-saving swaps back with no animation.
        <motion.span
          animate={{ scale: 1 }}
          className="flex"
          initial={{ scale: 0.85 }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { type: "spring", stiffness: 500, damping: 26, mass: 0.5 }
          }
        >
          {/* Passed as `color`, not a text utility: Slate applies its default
              icon colour inline, which outranks a class. Literal rather than a
              token because the closest match, `message-icon-critical`, means
              "something is wrong" — wrong semantics for a save. */}
          <HeartSolid
            className={iconStyle.cls({ size })}
            color="#921100"
            titleAccess=""
          />
        </motion.span>
      ) : (
        <Heart className={iconStyle.cls({ size })} titleAccess="" />
      )}
    </button>
  );
};

const buttonStyle = tsv({
  base: [
    "border-fs-border-muted flex cursor-pointer items-center justify-center",
    "bg-fs-surface-primary rounded-full border",
    "shadow-[0px_0px_1px_0px_rgba(0,0,0,0.15)]",
  ],
  variants: {
    size: {
      medium: "size-8",
      small: "size-7",
    },
  },
});

const iconStyle = tsv({
  base: "",
  variants: {
    size: {
      medium: "size-5",
      small: "size-4",
    },
  },
});

/** Fades in and rises the last few pixels on hover, reversing on the way out. */
const revealStyle = tsv({
  base: [
    "transition-[opacity,translate] duration-200 ease-out",
    "motion-reduce:transition-none",
  ],
  variants: {
    saved: {
      true: "translate-y-0 opacity-100",
      false: [
        "translate-y-1 opacity-0",
        // Delay on hover only: entering is unhurried, leaving is immediate.
        // A symmetric delay leaves the heart trailing the pointer.
        "group-hover:translate-y-0 group-hover:opacity-100",
        "group-hover:delay-[120ms]",
      ],
    },
  },
});
